import React, { useState, useEffect } from "react";
import { Card, Form, Button, Space, Typography, Spin, InputNumber, Switch } from "antd";
import { GiftOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../shared/utils/Util";
import { getFetchT, fetchT } from "../../shared/utils/TypedUtil";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import { PointsConfig, UpdatePointsConfigRequest } from "./types";
import { effectivePoints } from "./points";

const { Text } = Typography;

// Sample price (in cents) used for the live redeem-rate preview.
const PREVIEW_PRICE_CENTS = 1000;

type PointsConfigEditorProps = {
  restaurantId: string;
};

export const PointsConfigEditor: React.FC<PointsConfigEditorProps> = ({ restaurantId }) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { updateSuccess, notifyError } = useNotification();
  const [config, setConfig] = useState<PointsConfig | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const enabled = Form.useWatch("enabled", form);
  const ratePerDollar = Form.useWatch("rate_per_dollar", form);

  const refreshConfig = () => {
    getFetchT<PointsConfig>(
      `${API_URL}/v1/r/${restaurantId}/points_config`,
      (result) => {
        setConfig(result);
        form.setFieldsValue({
          enabled: result.enabled ?? false,
          rate_per_dollar: result.rate_per_dollar ?? null,
        });
      },
      (err) => {
        notifyError(t("failed_to_fetch_points_config"), err);
        setConfig({} as PointsConfig);
      }
    );
  };

  useEffect(() => {
    refreshConfig();
  }, [restaurantId]);

  const onFinish = (values: { enabled?: boolean; rate_per_dollar?: number | null }) => {
    setLoading(true);

    const request: UpdatePointsConfigRequest = {
      enabled: values.enabled ?? false,
      rate_per_dollar: values.rate_per_dollar ?? null,
    };

    fetchT<PointsConfig>(
      "PUT",
      `${API_URL}/v1/r/${restaurantId}/points_config`,
      request,
      () => {
        updateSuccess(t("points_config"));
        setLoading(false);
        refreshConfig();
      },
      (err) => {
        notifyError(t("failed_to_update_points_config"), err);
        setLoading(false);
      }
    );
  };

  if (config === undefined) {
    return <Spin size="large" />;
  }

  const previewPoints = effectivePoints(PREVIEW_PRICE_CENTS, ratePerDollar ?? undefined);

  return (
    <div>
      <Card
        title={
          <Space>
            <GiftOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("points_config")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="enabled"
            label={t("use_automatic_redeem_rate")}
            valuePropName="checked"
            tooltip={t("use_automatic_redeem_rate_description")}
          >
            <Switch checkedChildren={t("yes")} unCheckedChildren={t("no")} />
          </Form.Item>

          <Form.Item
            name="rate_per_dollar"
            label={t("redeem_rate")}
            tooltip={t("redeem_rate_description")}
            dependencies={["enabled"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!getFieldValue("enabled")) {
                    return Promise.resolve();
                  }
                  if (value === undefined || value === null) {
                    return Promise.reject(new Error(t("redeem_rate_required_when_enabled")));
                  }
                  if (value <= 0) {
                    return Promise.reject(new Error(t("redeem_rate_must_be_positive")));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber
              min={1}
              step={1}
              precision={0}
              disabled={!enabled}
              addonAfter={t("points_per_dollar")}
              style={{ width: "100%", maxWidth: 320 }}
            />
          </Form.Item>

          {enabled && previewPoints !== undefined ? (
            <Text type="secondary">{t("redeem_rate_preview", { points: previewPoints })}</Text>
          ) : null}

          <Form.Item style={{ marginTop: token.marginLG, marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={loading}>
              {t("save")}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};
